import React from 'react';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';

import {
  loadPdfJsWorker,
  widgetsFromAnnotations,
  type PdfJs,
  type PdfWidget,
  type WidgetAnnotation
} from '~/components/workspaces/lib/template-form-pdf';

export type TemplateFormDownload = {
  /** null until the download completes. */
  pdf: Blob | null;
  error: Error | null;
};

const NOT_DOWNLOADED: TemplateFormDownload = { pdf: null, error: null };

const asError = (err: unknown) => (err instanceof Error ? err : new Error(String(err)));

/**
 * Downloads the PDF at url, or nothing while url is null. The focus starts it
 * as it opens, so statics renders the form while the editor's code and state
 * load rather than after them, and a form the editor cannot edit is previewed
 * from the same download instead of being rendered again.
 */
export function useTemplateFormDownload(url: string | null): TemplateFormDownload {
  const [result, setResult] = React.useState<(TemplateFormDownload & { url: string }) | null>(null);

  React.useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    const { signal } = controller;

    (async () => {
      const response = await fetch(url, { credentials: 'include', signal });
      if (!response.ok) throw new Error(`Loading the form failed with status ${response.status}`);
      // Typed as a PDF whatever statics answered, because the preview frames
      // it from a blob URL on the app's own origin.
      const pdf = new Blob([await response.blob()], { type: 'application/pdf' });
      if (!signal.aborted) setResult({ url, pdf, error: null });
    })().catch((err: unknown) => {
      if (!signal.aborted) setResult({ url, pdf: null, error: asError(err) });
    });

    return () => controller.abort();
  }, [url]);

  return result?.url === url ? result : NOT_DOWNLOADED;
}

export type TemplateFormPdfPage = {
  page: PDFPageProxy;
  /** CSS pixels at scale 1. */
  width: number;
  height: number;
  widgets: PdfWidget[];
};

type TemplateFormPdf = {
  pdfjs: PdfJs;
  document: PDFDocumentProxy;
  pages: TemplateFormPdfPage[];
};

const loadPage = async (document: PDFDocumentProxy, number: number) => {
  const page = await document.getPage(number);
  const viewport = page.getViewport({ scale: 1 });
  const annotations = (await page.getAnnotations({ intent: 'display' })) as WidgetAnnotation[];
  return {
    page,
    width: viewport.width,
    height: viewport.height,
    widgets: widgetsFromAnnotations(annotations, (rect) =>
      viewport.convertToViewportRectangle(rect)
    )
  };
};

const loadPages = (document: PDFDocumentProxy) =>
  Promise.all(
    Array.from({ length: document.numPages }, (_, index) => loadPage(document, index + 1))
  );

/**
 * Loads a downloaded form with pdf.js, or nothing while data is null. When
 * data changes the previous document stays on screen until its successor has
 * loaded, so saving a version does not blank the form while statics renders
 * the next one.
 */
export function useTemplateFormPdf(data: Blob | null) {
  const [pdf, setPdf] = React.useState<TemplateFormPdf | null>(null);
  const [failure, setFailure] = React.useState<{ data: Blob; error: Error } | null>(null);

  React.useEffect(() => {
    if (!data) return;
    let cancelled = false;
    // Opened by this load and not yet handed to pdf.
    let opened: PDFDocumentProxy | null = null;

    (async () => {
      // pdf.js takes over the buffer it is given, so it parses a copy and
      // data stays whole for the preview.
      const [{ pdfjs, worker }, bytes] = await Promise.all([loadPdfJsWorker(), data.arrayBuffer()]);
      if (cancelled) return;

      // A load is closed only once pdf.js has opened it: a task destroyed
      // while the worker is still opening its document never tells the worker,
      // which would hold the document for as long as the page shares it.
      const task = pdfjs.getDocument({
        data: new Uint8Array(bytes),
        worker,
        isEvalSupported: false
      });
      const document = await task.promise.catch((err: unknown) => {
        void task.destroy();
        throw err;
      });
      if (cancelled) {
        void document.destroy();
        return;
      }
      opened = document;
      const pages = await loadPages(document);
      if (cancelled) return;
      opened = null;
      setPdf({ pdfjs, document, pages });
    })().catch((err: unknown) => {
      void opened?.destroy();
      opened = null;
      if (!cancelled) setFailure({ data, error: asError(err) });
    });

    return () => {
      cancelled = true;
      void opened?.destroy();
      opened = null;
    };
  }, [data]);

  // Runs when a successor replaces pdf, and on unmount.
  React.useEffect(
    () => () => {
      void pdf?.document.destroy();
    },
    [pdf]
  );

  return { pdf, error: failure?.data === data ? failure.error : null };
}
